import {
  fetchAccApplyGame,
  fetchAccApplyResult,
  fetchAccApplySelfReport,
  fetchAccApplyTip,
  fetchAccApplyVideo,
  fetchAccCompetenceCurationCompany,
  fetchAccCompetenceCurationJob,
  fetchAccCompetenceProfile,
  fetchAccStatusUser,
} from '@repository/publicAcc/usePublicAcc';
import { createStore } from './utils';
import { AccResultRecordVO } from '@domain/vo/acca/v1/test/result/AccResultVO';
import { PublicAccApplyUserStatusVO } from '@domain/vo/acca/v1/test/result/PublicAccApplyUserStatusVO';
import { AccApplyResultVO } from '@domain/vo/acca/v1/test/result/apply/AccApplyResultVO';
import { AccSelfReportResultVO } from '@domain/vo/acca/v1/test/result/apply/AccSelfReportResultVO';
import { AccVideoDetailResultVO, AccVideoResultVO } from '@domain/vo/acca/v1/test/result/apply/AccVideoResultVO';
import { AccGameResultVO } from '@domain/vo/acca/v1/test/result/apply/AccGameResultVO';
import { AccApplyTipVO } from '@domain/vo/acca/v1/test/result/apply/AccApplyTipVO';
import { AccProfileResultVO } from '@domain/vo/acca/v1/test/result/competence/AccProfileResultVO';
import { AccCompanyCurationVO } from '@domain/vo/acca/v1/test/result/competence/AccCompanyCurationVO';
import { AccJobCurationVO } from '@domain/vo/acca/v1/test/result/competence/AccJobCurationVO';
import { PublicAccApplyUserStatusRs } from '@domain/rs/acca/v1/test/result/PublicAccApplyUserStatusRs';
import { VideoType } from '@domain/constant/videoType';
import UrlUtil from '@utils/urlUtil';

export interface AccaPublicTestResultStoreType {
  code: string;
  accResultSn: number;
  accResult: AccResultRecordVO | null;
  publicAccApplyUserStatus: PublicAccApplyUserStatusVO | null;

  setAccResultSn: (accResultSn: number) => void;
  fetchAccStatusUser: () => Promise<PublicAccApplyUserStatusRs>;

  /** 신역검 응시 분석 결과 **/
  accApplyResult: AccApplyResultVO | null;
  accSelfReportResult: AccSelfReportResultVO | null;
  accVideoResult: AccVideoResultVO | null;
  accVideoDetailMap: Map<VideoType, AccVideoDetailResultVO[]> | null;
  accGameResult: AccGameResultVO | null;
  accApplyTip: AccApplyTipVO | null;

  fetchAccApplyResult: () => Promise<void>;
  fetchAccApplySelfReport: () => Promise<void>;
  fetchAccApplyVideo: () => Promise<void>;
  fetchAccApplyGame: () => Promise<void>;
  fetchAccApplyTip: () => Promise<void>;

  /** 신역검 역량 분석 결과 **/
  accProfileResult: AccProfileResultVO | null;
  accCompanyCuration: AccCompanyCurationVO | null;
  accJobCuration: AccJobCurationVO | null;

  fetchAccCompetenceProfile: () => Promise<void>;
  fetchAccCompetenceCurationCompany: () => Promise<void>;
  fetchAccCompetenceCurationJob: () => Promise<void>;
}

export const useAccaPublicTestResultStore = createStore<AccaPublicTestResultStoreType>((set, get) => ({
  code: UrlUtil.getParamFromUrl().code || '',
  accResultSn: -1,
  accResult: null,
  publicAccApplyUserStatus: null,

  accApplyResult: null,
  accSelfReportResult: null,
  accVideoResult: null,
  accVideoDetailMap: null,
  accGameResult: null,
  accApplyTip: null,

  accProfileResult: null,
  accCompanyCuration: null,
  accJobCuration: null,

  setAccResultSn: (accResultSn: number) => {
    set((state) => {
      state.accResultSn = accResultSn;
    });
  },
  fetchAccStatusUser: async () => {
    const code = get().code ?? UrlUtil.getParamFromUrl().code;
    const accResult = await fetchAccStatusUser(code);
    set((state) => {
      state.publicAccApplyUserStatus = new PublicAccApplyUserStatusVO(accResult);
      state.accResult = new AccResultRecordVO({
        resultType: 'VERY_GOOD',
        analysisType: accResult.analysisType,
        sn: 0,
        code: '',
        name: accResult.name,
        externalYn: false,
        applicationDate: '',
        version: accResult.version,
        thumbnailUrl: '',
      });
    });

    return accResult;
  },

  fetchAccApplyResult: async () => {
    const code = get().code;
    const accApplyResult = await fetchAccApplyResult(code);
    set((state) => {
      state.accApplyResult = accApplyResult;
    });
  },
  fetchAccApplySelfReport: async () => {
    const code = get().code;
    const accSelfReportResult = await fetchAccApplySelfReport(code);
    set((state) => {
      state.accSelfReportResult = accSelfReportResult;
    });
  },
  fetchAccApplyVideo: async () => {
    const code = get().code;
    const accVideoResult = await fetchAccApplyVideo(code);
    const accVideoDetailMap = new Map<VideoType, AccVideoDetailResultVO[]>();
    accVideoResult?.details?.forEach((detail) => {
      const { type } = detail;
      accVideoDetailMap?.set(type, [...(accVideoDetailMap?.get(type) || []), detail]);
    });
    set((state) => {
      state.accVideoResult = accVideoResult;
      state.accVideoDetailMap = accVideoDetailMap;
    });
  },
  fetchAccApplyGame: async () => {
    const code = get().code;
    const accGameResult = await fetchAccApplyGame(code);
    set((state) => {
      state.accGameResult = accGameResult;
    });
  },
  fetchAccApplyTip: async () => {
    const code = get().code;
    const accApplyTip = await fetchAccApplyTip(code);
    set((state) => {
      state.accApplyTip = accApplyTip;
    });
  },
  fetchAccCompetenceProfile: async () => {
    const code = get().code;
    const accProfileResult = await fetchAccCompetenceProfile(code);
    set((state) => {
      state.accProfileResult = accProfileResult;
    });
  },
  fetchAccCompetenceCurationCompany: async () => {
    const code = get().code;
    const accCompanyCuration = await fetchAccCompetenceCurationCompany(code);
    set((state) => {
      state.accCompanyCuration = accCompanyCuration;
    });
  },
  fetchAccCompetenceCurationJob: async () => {
    const code = get().code;
    const accJobCuration = await fetchAccCompetenceCurationJob(code);
    set((state) => {
      state.accJobCuration = accJobCuration;
    });
  },
}));
